import React from 'react';
import { maxBy, sortBy } from 'lodash';
import cloneDeep from 'lodash.clonedeep';
import { Container, Row, Col } from 'react-bootstrap';

import { Card } from '../../../../../components/card';
import { PlayerContext } from '../../../../../contexts/player';
import { OverlayContext } from '../../../../../contexts/overlay';
import { GetTaskById } from '../../../../../utils/game-engine/assets';
import { GameDocumentContext } from '../../../../../contexts/game-document';
import { EnterGameButton } from '../../../../../components/enter-game-button';
import { UpdatePlayerStateAsync } from '../../../../../utils/game-engine/base';
import { GetResourceValue } from '../../../../../utils/game-document/resources';
import { DisplayLanguageContext } from '../../../../../contexts/display-languages';
import { TaskContent } from '../../../../../types/game-document/entities/task-content';
import { generateTitleById } from '../../../../../utils/game-document/display-languages';

type ODT = TaskContent & { totalScore: number };
const taskImage =
  'https://stsharedappstorage.blob.core.windows.net/prod-catalyst-files/icons/on-demand-task.png';

export const OnDemandTask: React.FC = () => {
  const [game] = React.useContext(GameDocumentContext);
  const [player, setPlayer] = React.useContext(PlayerContext);
  const [, setOverlay] = React.useContext(OverlayContext);
  const [displayLanguageContext] = React.useContext(DisplayLanguageContext);

  const taskContents =
    game.gameDocument?.assets.taskContents?.filter(
      (t) => typeof t.onDemandTaskCategory === 'string'
    ) ?? [];
  const ODTCategories = game.gameDocument?.assets.onDemandTaskCategories ?? [];
  const completedTask = (player.playerState?.tasks ?? [])
    .filter((t) => t.status === 'completed')
    .map((t) => t.id);

  const ODTListByCategory = React.useMemo(() => {
    if (!taskContents.length) return [];

    return sortBy(
      ODTCategories.map((c) => {
        const tasks: ODT[] = [];

        taskContents.forEach((t) => {
          if (t.onDemandTaskCategory === c && !completedTask.includes(t.id)) {
            const result: ODT = cloneDeep({ ...t, totalScore: 0 });
            if (t.forms && t.forms.length) {
              t.forms.forEach((f) => {
                if (f.type === 'checkbox') {
                  f.answers?.forEach(
                    (a) => (result.totalScore += a.score ?? 0)
                  );
                } else if (f.type === 'radio') {
                  result.totalScore +=
                    maxBy(f.answers, (a) => a.score)?.score ?? 0;
                } else {
                  result.totalScore += f.score ?? 0;
                }
              });
            }

            if (t.ARMiniGameSetting) {
              result.totalScore += t.ARMiniGameSetting.score ?? 0;
            }
            tasks.push(result);
          }
        });

        return {
          name: c,
          tasks
        };
      }).filter((odt) => odt.tasks.length),
      (odt) => odt.name
    );
  }, [ODTCategories, taskContents]);

  const availableScore =
    ODTListByCategory.length > 0
      ? ODTListByCategory.flatMap((odt) =>
          odt.tasks.map((t) => t.totalScore)
        ).reduce((prev, next) => prev + next)
      : 0;

  const isInLobby = ['waiting', 'ready'].includes(
    player.playerState?.status ?? ''
  );
  const isUnableToOpen =
    (player.playerState?.openedTask ?? []).length > 0 ||
    player.playerState?.openedOnDemandTask ||
    isInLobby;

  const onHandleClick = (odt: ODT) => {
    const isExist = GetTaskById(player, odt.id);
    setPlayer((prev) =>
      UpdatePlayerStateAsync(prev, {
        ...prev.playerState!,
        ...(!isExist && {
          tasks: [
            ...(prev.playerState?.tasks ?? []),
            {
              id: odt.id
            }
          ]
        }),
        openedOnDemandTask: odt.id
      })
    );
    setOverlay((prev) => ({
      ...prev,
      drawerIsOpen: false
    }));
  };

  return (
    <>
      <Container className={'drawer__body'} fluid>
        <Row className={'mb-4 align-items-center border-bottom pb-3'}>
          <Col>
            <h1 className={'m-0 fw-bold'}>
              {generateTitleById(
                '47750496-640c-46c5-945c-0a72fcc7a259',
                game,
                displayLanguageContext.displayLanguageSelected.resources!,
                'game'
              ) || 'On-demand tasks'}
            </h1>
          </Col>
          <Col xs={'auto'}>
            <span className={'text-primary'}>
              <b>{availableScore}</b>{' '}
              {generateTitleById(
                '7e1f2e3d-4c5b-6a7e-8f9d-0a1b2c3d4e5f',
                game,
                displayLanguageContext.displayLanguageSelected.resources!,
                'game'
              ) || 'points available'}
            </span>
          </Col>
        </Row>

        <div className={'pb-3'}>
          {generateTitleById(
            '8f2a3b4c-5d6e-7f8a-9b0c-1d2e3f4a5b6c',
            game,
            displayLanguageContext.displayLanguageSelected.resources!,
            'game'
          ) || 'Complete these tasks at any time for additional points.'}
        </div>

        <div className={'d-flex flex-column gap-3'}>
          {ODTListByCategory.map((category) => (
            <div key={category.name} className={'d-flex flex-column'}>
              <span className={'fw-bold'}>{category.name}</span>
              <div className="grid-container">
                {category.tasks.map((odt) => (
                  <div key={odt.id} className="grid-item">
                    <Card
                      style={{
                        cursor: isUnableToOpen ? 'not-allowed' : 'pointer',
                        opacity: isUnableToOpen ? 0.5 : 1,
                        textAlign: 'center'
                      }}
                      imageUrl={taskImage}
                      title={GetResourceValue(
                        game.gameDocument!,
                        odt.titleResId ?? '',
                        player.playerState?.language.name ?? ''
                      )}
                      subTitle={`${odt.totalScore}pts`}
                      {...(!isUnableToOpen && {
                        onClick: () => onHandleClick(odt)
                      })}
                    />
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </Container>
      <EnterGameButton />
    </>
  );
};
